/* System defined core library */
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import * as momentTimezone from 'moment-timezone';

/* User defined library/components */
import { UtilityService } from './utility.service';

/* User defined models */
import { TimeZoneMap } from '../models/common/timeZoneData.model';
import { AppointmentDetail } from '../models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../models/scheduling/appointmentSlot.model';
import { BatteryConfig } from '../models/scheduling/batteryConfig.model';
import { Month } from '../models/scheduling/month.model';
import { Slot } from '../models/scheduling/slot.model';
import { Year } from '../models/scheduling/year.model';

/* User defined enums/constants */
import { TargetFlow } from '../constants/scheduling.constant';
import { TimeZoneMapping } from '../constants/timeZoneMaps.constant';


@Injectable()
export class DateTimeService {
  // Static Methods
  public static get Months(): Month[] {
    // Increment date by 1 as months are zero indexed

    const months: Month[] = [];

    for (let i = 1; i <= 12; i++) {
      months.push(new Month(i));
    }

    return months;
  }


  /**********************************************************
   * Years for credit card payment expiration year dropdown *
   * Returns an array of 20 years (current + 19)            *
   **********************************************************/
  public static get Years(): Year[] {
    const curYear = new Date().getFullYear();
    const years: Year[] = [];

    for (let i = curYear; i <= (curYear + 19); i++) {
      years.push(new Year(i));

    }
    return years;
  }

  public static formatDateTimeForPost(date: Date): string {
    const monthNumber: number = date.getMonth() + 1;
    const month = monthNumber > 9 ? monthNumber : '0' + monthNumber;
    const day = date.getDate() > 9 ? date.getDate() : '0' + date.getDate();
    const hr = date.getHours() > 9 ? date.getHours() : '0' + date.getHours();
    const min = date.getMinutes() > 9 ? date.getMinutes() : '0' + date.getMinutes();
    return `${date.getFullYear()}-${month}-${day} ${hr}:${min}`;
  }

  public static convertSecToMSS(m: number): string {
    const min = Math.floor((m / 60) % 60);
    const sec = Math.floor(m % 60);
    return min + ':' + (sec < 10 ? '0' : '') + sec;
  }

  /* Constructor declaration with required injected services */
  constructor(
    private utilityService: UtilityService
  ) {

  }

  /**
   * This function returns true if the apptointment is applicable to cancel or reschedule
   * @param dateToCompare
   */
  compareDate(dateToCompare: Date): boolean {
    const currentDate = moment();
    const apptDate = moment(dateToCompare);
    return currentDate.valueOf() < apptDate.valueOf();
  }

  /**
   * Returns the combined duration of all selected appointments or 0 if total duration is not to be displayed in that flow
   * @param appointmentDetail
   * @param isSubmitted
   * @param targetFlow
   */
  public getTotalDuration(
    appointmentData: AppointmentDetail[],
    isSubmitted: boolean,
    targetFlow: string,
    batteryConfig?: BatteryConfig
  ): number {
    let duration = 0;
    if (appointmentData && appointmentData.length) {
      let showDurationWhenSubmitted: boolean = isSubmitted && targetFlow !== TargetFlow.Lpi;
      appointmentData.forEach((appointment: AppointmentDetail) => {
        if (appointment && (appointment.selected || showDurationWhenSubmitted) && appointment.appointmentSlot) {
          duration += appointment.appointmentSlot.duration;
        }
      });
    }
    if (batteryConfig && batteryConfig.bufferTime) duration += batteryConfig.bufferTime;
    return duration;
  }

  /**
   * sets slot times on appointments
   * @param appointmentSlot
   * @param index
   * @param appointmentDetail
   * @param timeFormat
   */
  setAppointmentSlotTimes(
    appointmentSlot: AppointmentSlot,
    index: number,
    appointmentDetail: AppointmentDetail,
    timeFormat: boolean
  ): AppointmentDetail {
    appointmentDetail.appointmentSlot = appointmentSlot;
    appointmentDetail.appointmentSlot.testTime = this.utilityService.getTimeString(appointmentDetail.appointmentSlot.startDateTime, timeFormat);
    appointmentDetail.appointmentSlot.startDateTime = new Date(this.utilityService.getFormatToFullMMDDYYYY(appointmentDetail.appointmentSlot.startDateTime, "/"));
    appointmentDetail.appointmentSlot.testEndTime = this.utilityService.getStartEndFormat(
      UtilityService.addMins(
        appointmentDetail.appointmentSlot.startDateTime,
        appointmentSlot.duration
      )
    );

    //set multiday slot info
    if (appointmentDetail.exam.sessions != undefined && appointmentDetail.exam.sessions.length) {
      appointmentDetail.exam.sessions[index].site = appointmentSlot.site;
      appointmentDetail.exam.sessions[index].duration = appointmentSlot.duration;
      appointmentDetail.exam.sessions[index].selectedDate = moment(appointmentDetail.appointmentSlot.startDateTime).format('YYYY-MM-DDTHH:mm:ss')
      appointmentDetail.exam.sessions[index].slot = new Slot(
        appointmentSlot.id, appointmentSlot.href,
        appointmentDetail.appointmentSlot.startDateTime,
        null,
        appointmentDetail.appointmentSlot.testTime,
        appointmentDetail.appointmentSlot.testEndTime,
        appointmentSlot.duration);
    }
    return appointmentDetail;
  }

  /**
   * Returns true if the appointment date is not in the past and in correct flow
   * @param startDateTime
   * @param targetFlow
   */
  validDateToCancel(
    startDateTime: Date,
    targetFlow: string
  ): boolean {
    const apptDate = moment(startDateTime);
    const apptTime = moment(apptDate).startOf('day');
    const now = moment();

    return !(now.valueOf() > apptTime.valueOf() && (targetFlow === TargetFlow.Cancel || targetFlow === TargetFlow.Lpi));
  }

  /**
   * get browser time zone name and convert to windows timezone (ie; Pacific Standard Time)
   */
  public getBrowserTimeZone(): string {
    const iana = momentTimezone.tz.guess(true);

    return TimeZoneMapping.supplementalData.windowsZones.mapTimezones.mapZone.find((tz: TimeZoneMap) => {
      return tz.ianaArray.some((name: string) => {
        return name === iana;
      });
    }).windowsTz;
  }
}
